/**
 * Onboard Client Page - Microsoft 365 Style
 * Generate onboarding links and manage client submissions
 */

import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  makeStyles,
  Text,
  Button,
  Input,
  Badge,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Field,
  Tooltip,
} from '@fluentui/react-components';
import {
  SearchRegular,
  LinkRegular,
  CopyRegular,
  CheckmarkRegular,
  ClockRegular,
  CheckmarkCircleRegular,
  DocumentRegular,
  EyeRegular,
  DeleteRegular,
  DismissRegular,
} from '@fluentui/react-icons';
import { ContentCard } from '../../../components/layout';
import { SearchableDropdown, TablePagination } from '../../../components/common';
import { usePagination } from '../../../hooks/usePagination';
import { ApiClient, newToast } from '../../../configurations/index.config';
import { getErrorMessage } from '../../../types';

const useStyles = makeStyles({
  pageHeader: {
    marginBottom: '24px',
  },
  pageTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '4px',
    display: 'block',
  },
  pageSubtitle: {
    fontSize: '14px',
    color: '#605e5c',
    display: 'block',
  },
  linkGeneratorSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  linkGeneratorRow: {
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-end',
  },
  companyNameInput: {
    flex: 1,
    maxWidth: '400px',
  },
  generatedLinkContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    backgroundColor: '#f3f2f1',
    borderRadius: '8px',
    border: '1px solid #e1dfdd',
  },
  generatedLink: {
    flex: 1,
    fontFamily: 'monospace',
    fontSize: '13px',
    color: '#323130',
    wordBreak: 'break-all',
  },
  dashboardCards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '16px',
    marginBottom: '24px',
  },
  statCard: {
    padding: '20px',
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    border: '1px solid #e1dfdd',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  statIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
  },
  statContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  statValue: {
    fontSize: '28px',
    fontWeight: '600',
    color: '#323130',
  },
  statLabel: {
    fontSize: '13px',
    color: '#605e5c',
  },
  filterSection: {
    marginBottom: '24px',
  },
  filterGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr) auto auto',
    gap: '12px',
    alignItems: 'end',
  },
  searchBar: {
    marginBottom: '16px',
    maxWidth: '400px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  tableHeader: {
    backgroundColor: '#f5f5f5',
    borderBottom: '2px solid #e1dfdd',
  },
  th: {
    padding: '12px 16px',
    textAlign: 'left',
    fontSize: '13px',
    fontWeight: '600',
    color: '#323130',
  },
  tr: {
    borderBottom: '1px solid #e1dfdd',
    ':hover': {
      backgroundColor: '#f9f9f9',
    },
  },
  td: {
    padding: '12px 16px',
    fontSize: '14px',
    color: '#323130',
  },
  emptyState: {
    padding: '60px 20px',
    textAlign: 'center',
    color: '#605e5c',
  },
  actionButtons: {
    display: 'flex',
    gap: '8px',
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: '600',
    marginBottom: '12px',
    display: 'block',
  },
});

interface OnboardingSubmission {
  id: number;
  onboard_link_id: number;
  client_name: string;
  location_address: string;
  contact_number: string;
  email_address: string;
  postal_address?: string;
  services_required: string[];
  premises_types: string[];
  guards_day: number;
  guards_night: number;
  guards_total: number;
  date_commencement: string;
  duration_value: number;
  duration_unit: string;
  billing_name: string;
  billing_position?: string;
  billing_tel: string;
  billing_email: string;
  other_duties?: string[];
  status: 'pending' | 'approved' | 'rejected';
  submitted_at: string;
  created_at: string;
}

export const OnboardClientFluentUI: React.FC = () => {
  const styles = useStyles();
  const [submissions, setSubmissions] = useState<OnboardingSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Link generation state
  const [companyName, setCompanyName] = useState('');
  const [generatedLink, setGeneratedLink] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  // Filter state
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');

  const navigate = useNavigate();

  // Dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState<OnboardingSubmission | null>(null);

  // Statistics
  const [stats, setStats] = useState({
    pendingApproval: 0,
    totalApproved: 0,
    totalSubmissions: 0,
  });

  // Filtered submissions
  const filteredSubmissions = useMemo(() => {
    let filtered = [...submissions];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        s =>
          s.client_name?.toLowerCase().includes(query) ||
          s.email_address?.toLowerCase().includes(query) ||
          s.location_address?.toLowerCase().includes(query) ||
          s.contact_number?.toLowerCase().includes(query)
      );
    }

    // Status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(s => s.status === filterStatus);
    }

    // Date range filter
    if (filterStartDate) {
      filtered = filtered.filter(s => new Date(s.submitted_at) >= new Date(filterStartDate));
    }
    if (filterEndDate) {
      filtered = filtered.filter(s => new Date(s.submitted_at) <= new Date(filterEndDate));
    }

    return filtered;
  }, [submissions, searchQuery, filterStatus, filterStartDate, filterEndDate]);

  const { paginatedData, currentPage, totalItems, itemsPerPage, setCurrentPage, setItemsPerPage } =
    usePagination<OnboardingSubmission>({
      data: filteredSubmissions,
      initialItemsPerPage: 10,
    });

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const fetchSubmissions = async () => {
    setLoading(true);
    try {
      const response = await ApiClient.get('/get-onboarding-submissions');
      if (response.data.status === 'success') {
        const data = response.data.data || [];
        setSubmissions(data);

        // Calculate statistics
        const pending = data.filter((s: OnboardingSubmission) => s.status === 'pending').length;
        const approved = data.filter((s: OnboardingSubmission) => s.status === 'approved').length;

        setStats({
          pendingApproval: pending,
          totalApproved: approved,
          totalSubmissions: data.length,
        });
      } else {
        newToast('error', response.data.message || 'Failed to fetch submissions');
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      const message = err.response?.data?.message || 'Failed to fetch submissions';
      console.error('Fetch submissions error:', message, error);
    }
    setLoading(false);
  };

  const handleGenerateLink = async () => {
    if (!companyName.trim()) {
      newToast('error', 'Please enter a company name');
      return;
    }

    setIsGenerating(true);
    try {
      const response = await ApiClient.post('/generate-onboard-link', {
        company_name: companyName.trim(),
      });

      if (response.data.status === 'success') {
        setGeneratedLink(response.data.data.link);
        newToast('success', 'Onboarding link generated successfully');
      }
    } catch (error: unknown) {
      // For now, generate a mock link for UI demonstration
      const baseUrl = window.location.origin;
      const token = btoa(`${companyName}-${Date.now()}`)
        .replace(/[^a-zA-Z0-9]/g, '')
        .substring(0, 32);
      setGeneratedLink(`${baseUrl}/onboard/${token}`);
      newToast('info', 'Demo link generated (API not ready)');
    }
    setIsGenerating(false);
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(generatedLink);
      setLinkCopied(true);
      newToast('success', 'Link copied to clipboard');
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (error) {
      newToast('error', 'Failed to copy link');
    }
  };

  const handleViewSubmission = (submission: OnboardingSubmission) => {
    navigate(`/account/submission-details/${submission.id}`);
  };

  const handleDeleteClick = (submission: OnboardingSubmission) => {
    setSelectedSubmission(submission);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedSubmission) {
      return;
    }

    try {
      await ApiClient.post('/delete-onboarding-submission', {
        id: selectedSubmission.id,
      });
      newToast('success', 'Submission deleted successfully');
      fetchSubmissions();
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setDeleteDialogOpen(false);
    setSelectedSubmission(null);
  };

  const clearFilters = () => {
    setFilterStatus('all');
    setFilterStartDate('');
    setFilterEndDate('');
    setSearchQuery('');
  };

  const hasActiveFilters =
    filterStatus !== 'all' || filterStartDate !== '' || filterEndDate !== '' || searchQuery !== '';

  const formatDateTime = (dateString: string) => {
    if (!dateString) {
      return '—';
    }
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return (
          <Badge appearance="filled" color="warning">
            Pending
          </Badge>
        );
      case 'approved':
        return (
          <Badge appearance="filled" color="success">
            Approved
          </Badge>
        );
      case 'rejected':
        return (
          <Badge appearance="filled" color="danger">
            Rejected
          </Badge>
        );
      default:
        return (
          <Badge appearance="outline" color="informative">
            {status}
          </Badge>
        );
    }
  };

  return (
    <div>
      <div className={styles.pageHeader}>
        <Text className={styles.pageTitle}>Onboard Client</Text>
        <Text className={styles.pageSubtitle}>
          Generate onboarding links and manage client submissions
        </Text>
      </div>

      {/* Link Generator Section */}
      <ContentCard>
        <div className={styles.linkGeneratorSection}>
          <Text className={styles.sectionTitle}>Generate Onboarding Link</Text>
          <div className={styles.linkGeneratorRow}>
            <Field label="Prospective Company Name" className={styles.companyNameInput}>
              <Input
                placeholder="Enter company name"
                value={companyName}
                onChange={e => setCompanyName(e.target.value)}
              />
            </Field>
            <Button
              appearance="primary"
              icon={<LinkRegular />}
              onClick={handleGenerateLink}
              disabled={isGenerating || !companyName.trim()}
            >
              {isGenerating ? 'Generating...' : 'Generate Onboard Link'}
            </Button>
          </div>

          {generatedLink && (
            <div className={styles.generatedLinkContainer}>
              <Text className={styles.generatedLink}>{generatedLink}</Text>
              <Tooltip content={linkCopied ? 'Copied!' : 'Copy to clipboard'} relationship="label">
                <Button
                  appearance="subtle"
                  icon={linkCopied ? <CheckmarkRegular /> : <CopyRegular />}
                  onClick={handleCopyLink}
                >
                  {linkCopied ? 'Copied' : 'Copy'}
                </Button>
              </Tooltip>
            </div>
          )}
        </div>
      </ContentCard>

      {/* Dashboard Cards */}
      <div className={styles.dashboardCards}>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ backgroundColor: '#fff4ce', color: '#d29200' }}>
            <ClockRegular />
          </div>
          <div className={styles.statContent}>
            <Text className={styles.statValue}>{stats.pendingApproval}</Text>
            <Text className={styles.statLabel}>Pending Approval</Text>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ backgroundColor: '#e8f5e9', color: '#388e3c' }}>
            <CheckmarkCircleRegular />
          </div>
          <div className={styles.statContent}>
            <Text className={styles.statValue}>{stats.totalApproved}</Text>
            <Text className={styles.statLabel}>Total Approved</Text>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ backgroundColor: '#e3f2fd', color: '#1976d2' }}>
            <DocumentRegular />
          </div>
          <div className={styles.statContent}>
            <Text className={styles.statValue}>{stats.totalSubmissions}</Text>
            <Text className={styles.statLabel}>Total Submissions</Text>
          </div>
        </div>
      </div>

      {/* Filter Section */}
      <ContentCard>
        <div className={styles.filterSection}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '12px',
            }}
          >
            <Text className={styles.sectionTitle} style={{ marginBottom: 0 }}>
              Filter Submissions
            </Text>
            {hasActiveFilters && (
              <Button
                appearance="subtle"
                size="small"
                icon={<DismissRegular />}
                onClick={clearFilters}
              >
                Clear Filters
              </Button>
            )}
          </div>
          <div className={styles.filterGrid}>
            <SearchableDropdown
              placeholder="Filter by Status"
              selectedValue={filterStatus}
              onSelect={value => setFilterStatus(value)}
              options={[
                { id: 'all', label: 'All Statuses' },
                { id: 'pending', label: 'Pending' },
                { id: 'approved', label: 'Approved' },
                { id: 'rejected', label: 'Rejected' },
              ]}
            />
            <Field label="Start Date">
              <Input
                type="date"
                value={filterStartDate}
                onChange={e => setFilterStartDate(e.target.value)}
              />
            </Field>
            <Field label="End Date">
              <Input
                type="date"
                value={filterEndDate}
                onChange={e => setFilterEndDate(e.target.value)}
              />
            </Field>
          </div>
        </div>
      </ContentCard>

      {/* Search Bar */}
      <div className={styles.searchBar} style={{ marginTop: '24px' }}>
        <Input
          placeholder="Search by company name, email, address, or phone..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          contentBefore={<SearchRegular />}
          size="medium"
        />
      </div>

      {/* Submissions Table */}
      <ContentCard noPadding>
        {loading ? (
          <div className={styles.emptyState}>
            <Text>Loading submissions...</Text>
          </div>
        ) : filteredSubmissions.length === 0 ? (
          <div className={styles.emptyState}>
            <Text size={500} weight="semibold" style={{ display: 'block' }}>
              No submissions found
            </Text>
            <Text style={{ marginTop: '8px', display: 'block' }}>
              {hasActiveFilters
                ? 'Try adjusting your search or filters'
                : 'No onboarding submissions yet. Generate a link and share it with prospective clients.'}
            </Text>
          </div>
        ) : (
          <table className={styles.table}>
            <thead className={styles.tableHeader}>
              <tr>
                <th className={styles.th}>Client Name</th>
                <th className={styles.th}>Location Address</th>
                <th className={styles.th}>Contact Number</th>
                <th className={styles.th}>Email</th>
                <th className={styles.th}>Date/Time Submitted</th>
                <th className={styles.th}>Status</th>
                <th className={styles.th} style={{ width: '150px' }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map(submission => (
                <tr key={submission.id} className={styles.tr}>
                  <td className={styles.td}>{submission.client_name}</td>
                  <td className={styles.td}>{submission.location_address}</td>
                  <td className={styles.td}>{submission.contact_number}</td>
                  <td className={styles.td}>{submission.email_address}</td>
                  <td className={styles.td}>{formatDateTime(submission.submitted_at)}</td>
                  <td className={styles.td}>{getStatusBadge(submission.status)}</td>
                  <td className={styles.td}>
                    <div className={styles.actionButtons}>
                      <Button
                        size="small"
                        appearance="subtle"
                        icon={<EyeRegular />}
                        onClick={() => handleViewSubmission(submission)}
                      >
                        View
                      </Button>
                      <Button
                        size="small"
                        appearance="subtle"
                        icon={<DeleteRegular />}
                        onClick={() => handleDeleteClick(submission)}
                      >
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {!loading && filteredSubmissions.length > 0 && (
          <TablePagination
            totalItems={totalItems}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={setItemsPerPage}
          />
        )}
      </ContentCard>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={(e, data) => setDeleteDialogOpen(data.open)}>
        <DialogSurface
          style={{
            maxWidth: '400px',
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <DialogBody>
            <DialogTitle>Delete Submission</DialogTitle>
            <DialogContent>
              <Text>
                Are you sure you want to delete the submission from "
                {selectedSubmission?.client_name}"? This action cannot be undone.
              </Text>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setDeleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button appearance="primary" onClick={handleDeleteConfirm}>
                Delete
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default OnboardClientFluentUI;
